import * as React from 'react';
import PositionMap from '../PositionMap';

import MapWithInfo from '~/components/MapWithInfo';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { useQuery } from '@tanstack/react-query';
import { AlertPriority, Device } from '@ixicare/resources';
import { useGPSGeofence, useLocation } from '~hooks/location';
import { formatDateTime } from '@ixicare/ui';
import { parseDate } from '~utils/date';
import { t } from '~/i18n';

interface AlertMapContainerProps {
  alert: Alert;
  parentAlert?: Alert;
}

const AlertMapContainer: React.FC<AlertMapContainerProps> = ({ alert, parentAlert }) => {
  if (!alert.device) return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  if (!alert.isOpen) return <OldLocationMap alert={alert} parentAlert={parentAlert} />;
  return <CurrentLocationMap alert={alert} parentAlert={parentAlert} />;
};

function ParentFallbackMap({ parentAlert, priority }: { parentAlert?: Alert; priority: AlertPriority }) {
  if (!parentAlert?.device?.location?.lastKnown) return null;
  const lastKnown = parentAlert.device.location.lastKnown;
  const label = t('alert.backup.locationOf', { name: parentAlert.description });
  const info = lastKnown.timestamp ? `${label} (${formatDateTime(parseDate(lastKnown.timestamp))})` : label;
  return (
    <MapWithInfo info={info}>
      <PositionMap locationData={lastKnown} isStatic={parentAlert.device.locationIsStatic ?? false} type={priority} />
    </MapWithInfo>
  );
}

function OldLocationMap({ alert, parentAlert }: AlertMapContainerProps) {
  const datetime = alert.acceptedTime ?? alert.cancelledTime ?? null;
  const deviceId = alert.device?.uuid ?? -1;
  let location = alert.device?.location;
  let locationTimestamp: Date | null = alert.created;
  if (!(alert.device?.locationIsStatic ?? false)) {
    location = useQuery({
      queryKey: ['device-location', deviceId, datetime?.toDateString()],
      queryFn: () => (datetime ? Device.getDeviceLocation(deviceId, datetime) : Device.getDeviceLastLocation(deviceId)),
      initialData: alert.device?.location ?? undefined,
    }).data;
    if (location?.lastKnown?.timestamp) {
      locationTimestamp = parseDate(location?.lastKnown?.timestamp);
    }
  }

  if (!location?.lastKnown) {
    return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  }

  let info = formatDateTime(locationTimestamp);
  if (location?.lastKnown?.description) {
    info = location?.lastKnown?.description + ` (${info})`;
  }
  return (
    <MapWithInfo info={info}>
      <PositionMap
        locationData={location?.lastKnown ?? undefined}
        isStatic={location?.isStatic ?? false}
        type={alert.priority}
      />
    </MapWithInfo>
  );
}

function CurrentLocationMap({ alert, parentAlert }: AlertMapContainerProps) {
  const geo = useGPSGeofence(alert.resident?.id);

  const locationData = useLocation(alert.device?.id, alert.device?.location?.lastKnown ?? undefined);

  if (!locationData) {
    return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  }

  const info =
    (locationData?.description ?? '') +
    (locationData?.timestamp ? ` (${formatDateTime(parseDate(locationData?.timestamp))})` : '');
  return (
    <MapWithInfo info={info}>
      <PositionMap
        locationData={locationData}
        isStatic={alert.device?.locationIsStatic ?? false}
        type={alert.priority}
        geofence={geo}
      />
    </MapWithInfo>
  );
}

export default observer(AlertMapContainer);
